import { useState } from 'react';
import { useIdeas } from '@/hooks/useIdeas';
import { PageShell, Section } from '@/components/layout';
import { IdeaCard } from '@/components/IdeaCard';
import { IdeaDetailSheet } from '@/components/IdeaDetailSheet';
import { WaterSheet } from '@/components/WaterSheet';
import { Skeleton } from '@/components/ui/skeleton';
import type { Idea, Mood } from '@/lib/types';

export default function Withered() {
  const { ideas, loading, waterIdea, updateIdea, archiveIdea, deleteIdea, setFocus, clearFocus } = useIdeas();
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [waterOpen, setWaterOpen] = useState(false);

  const witheredIdeas = ideas.filter(i => i.status === 'withered');

  const handleCardClick = (idea: Idea) => {
    setSelectedIdea(idea);
    setDetailOpen(true);
  };

  const handleWater = () => {
    setDetailOpen(false);
    setWaterOpen(true);
  };

  const handleWaterSubmit = async (reflection: string, nextStep?: string, mood?: Mood) => {
    if (!selectedIdea) return false;
    return waterIdea(selectedIdea.id, reflection, nextStep, mood);
  };

  return (
    <PageShell narrow>
      <header>
        <h1 className="text-xl font-medium text-foreground">Withered</h1>
        <p className="text-sm text-muted-foreground/70 mt-1">
          Ideas that have gone quiet.
        </p>
      </header>

      <Section>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
          </div>
        ) : witheredIdeas.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm text-muted-foreground/50">
              No withered ideas. Keep showing up.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {witheredIdeas.map(idea => (
              <IdeaCard key={idea.id} idea={idea} onClick={() => handleCardClick(idea)} />
            ))}
          </div>
        )}
      </Section>

      <IdeaDetailSheet 
        idea={selectedIdea} 
        open={detailOpen} 
        onOpenChange={setDetailOpen} 
        onUpdate={updateIdea} 
        onWater={handleWater} 
        onArchive={archiveIdea} 
        onDelete={deleteIdea}
        onSetFocus={setFocus}
        onClearFocus={clearFocus}
      />

      <WaterSheet 
        open={waterOpen} 
        onOpenChange={setWaterOpen} 
        onSubmit={handleWaterSubmit} 
        ideaTitle={selectedIdea?.title || undefined} 
        isTree={false} 
      />
    </PageShell>
  );
}
